import {
  BarChart3,
  BriefcaseBusiness,
  Building2,
  ClipboardCheck,
  DollarSign,
  FileText,
  GraduationCap,
  LayoutDashboard,
  LifeBuoy,
  LogIn,
  LogOut,
  Menu,
  MoveRight,
  PackageSearch,
  QrCode,
  ReceiptText,
  Settings,
  ShieldCheck,
  Tags,
  UserCheck,
  Users,
  X
} from "lucide-react";
import { useState, type ReactNode } from "react";
import logoImobi from "../../assets/logo_minimal.png";
import type { CompanyRole, GlobalRole } from "../types/imobi";

export type ViewId =
  | "login"
  | "company-select"
  | "dashboard"
  | "assets"
  | "asset-form"
  | "asset-detail"
  | "photos"
  | "companies"
  | "users"
  | "responsibles"
  | "company-transfers"
  | "external-services"
  | "depreciation"
  | "reports"
  | "audit"
  | "logs"
  | "settings"
  | "service-request"
  | "commercial-panel"
  | "service-catalog"
  | "feature-releases"
  | "inventory"
  | "mobile-scan"
  | "inventory-report"
  | "label-templates"
  | "label-print"
  | "contract-templates"
  | "training"
  | "support";

const navItems: Array<{ id: ViewId; label: string; icon: ReactNode }> = [
  { id: "dashboard", label: "Dashboard", icon: <LayoutDashboard size={18} /> },
  { id: "assets", label: "Bens", icon: <PackageSearch size={18} /> },
  { id: "inventory", label: "Balanço", icon: <ClipboardCheck size={18} /> },
  { id: "mobile-scan", label: "Leitura", icon: <QrCode size={18} /> },
  { id: "label-print", label: "Etiquetas", icon: <Tags size={18} /> },
  { id: "reports", label: "Relatórios", icon: <FileText size={18} /> },
  { id: "audit", label: "Auditoria", icon: <ShieldCheck size={18} /> },
  { id: "companies", label: "Empresas", icon: <Building2 size={18} /> },
  { id: "users", label: "Usuários", icon: <Users size={18} /> },
  { id: "responsibles", label: "Responsáveis", icon: <UserCheck size={18} /> },
  { id: "company-transfers", label: "Transferências", icon: <MoveRight size={18} /> },
  { id: "external-services", label: "Serviços externos", icon: <BriefcaseBusiness size={18} /> },
  { id: "depreciation", label: "Tipos e depreciação", icon: <BarChart3 size={18} /> },
  { id: "service-request", label: "Serviços", icon: <ReceiptText size={18} /> },
  { id: "commercial-panel", label: "Faturamento", icon: <DollarSign size={18} /> },
  { id: "training", label: "Treinamento", icon: <GraduationCap size={18} /> },
  { id: "support", label: "Suporte", icon: <LifeBuoy size={18} /> },
  { id: "settings", label: "Config.", icon: <Settings size={18} /> }
];

export function AppLayout({
  activeView,
  onNavigate,
  globalRole = "USER",
  companyRole,
  pendingTransferCount = 0,
  canSeeTransfers = false,
  canSeeAudit = false,
  canSeeCompanies = false,
  canSeeServices = false,
  canSeeExternalServices = false,
  canSeeTraining = false,
  topBar,
  mobileBellSlot,
  mobileUserSlot,
  helpButton,
  supportButton,
  onSignOut,
  children
}: {
  activeView: ViewId;
  onNavigate: (view: ViewId) => void;
  globalRole?: GlobalRole;
  companyRole?: CompanyRole;
  pendingTransferCount?: number;
  canSeeTransfers?: boolean;
  canSeeAudit?: boolean;
  canSeeCompanies?: boolean;
  canSeeServices?: boolean;
  canSeeExternalServices?: boolean;
  canSeeTraining?: boolean;
  topBar?: ReactNode;
  mobileBellSlot?: ReactNode;
  mobileUserSlot?: ReactNode;
  helpButton?: ReactNode;
  supportButton?: ReactNode;
  onSignOut?: () => void;
  children: ReactNode;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const canSeeUsers = globalRole === "MASTER" || globalRole === "ADMIN" || companyRole === "GERENTE";
  const visibleNavItems = navItems.filter((item) => {
    if (item.id === "users") return canSeeUsers;
    if (item.id === "companies") return canSeeCompanies;
    if (item.id === "service-request") return canSeeServices;
    if (item.id === "commercial-panel") return globalRole === "MASTER";
    if (item.id === "company-transfers") return canSeeTransfers;
    if (item.id === "external-services") return canSeeExternalServices;
    if (item.id === "training") return canSeeTraining;
    if (item.id === "audit") return canSeeAudit;
    return true;
  });
  const mobilePrimaryItems = visibleNavItems.slice(0, 3);
  const isActivePrimaryItem = mobilePrimaryItems.some((item) => item.id === activeView);

  function handleNavigate(view: ViewId) {
    setIsMobileMenuOpen(false);
    onNavigate(view);
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20 lg:pb-0">
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-64 border-r border-slate-200 bg-white lg:block">
        <div className="flex h-16 items-center gap-3 border-b border-slate-200 px-5">
          <img src={logoImobi} alt="IMOBI" className="h-11 w-11 rounded-lg object-contain" />
          <div>
            <p className="font-semibold text-ink">IMOBI</p>
            <p className="text-xs text-slate-500">v0.1.70</p>
          </div>
        </div>
        <nav className="h-[calc(100vh-4rem)] overflow-y-auto p-3">
          {visibleNavItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleNavigate(item.id)}
              className={`mb-1 flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium ${
                activeView === item.id ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {item.icon}
              {item.label}
              {item.id === "company-transfers" && pendingTransferCount > 0 ? (
                <span className="ml-auto rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white">{pendingTransferCount}</span>
              ) : null}
            </button>
          ))}
        </nav>
      </aside>

      <main className="lg:pl-64">
        {activeView !== "login" && topBar ? (
          <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:px-6">
            {topBar}
          </div>
        ) : null}
        {children}
      </main>

      {isMobileMenuOpen ? (
        <div className="fixed inset-x-0 bottom-[4.5rem] z-30 border-t border-slate-200 bg-white shadow-panel lg:hidden">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-ink">Menu</p>
              <p className="text-xs text-slate-500">IMOBI v0.1.70</p>
            </div>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Fechar menu"
              className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"
            >
              <X size={18} />
            </button>
          </div>
          <div className="grid max-h-[55vh] grid-cols-2 gap-2 overflow-y-auto p-3">
            {visibleNavItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavigate(item.id)}
                className={`flex items-center gap-3 rounded-md border px-3 py-3 text-left text-sm font-medium ${
                  activeView === item.id
                    ? "border-brand-200 bg-brand-50 text-brand-700"
                    : "border-slate-200 bg-white text-slate-600"
                }`}
              >
                {item.icon}
                {item.label}
                {item.id === "company-transfers" && pendingTransferCount > 0 ? (
                  <span className="ml-auto rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white">{pendingTransferCount}</span>
                ) : null}
              </button>
            ))}
            {onSignOut ? (
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onSignOut();
                }}
                className="col-span-2 flex items-center justify-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm font-semibold text-rose-700"
              >
                <LogOut size={18} />
                Sair
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-6 border-t border-slate-200 bg-white px-1 py-2 lg:hidden">
        {mobilePrimaryItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleNavigate(item.id)}
            className={`relative flex flex-col items-center gap-1 rounded-md px-1 py-1 text-[11px] font-medium ${
              activeView === item.id ? "text-brand-700" : "text-slate-500"
            }`}
          >
            {item.icon}
            {item.id === "company-transfers" && pendingTransferCount > 0 ? (
              <span className="absolute -mt-1 ml-5 rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white">{pendingTransferCount}</span>
            ) : null}
            {item.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen((current) => !current)}
          className={`flex flex-col items-center gap-1 rounded-md px-1 py-1 text-[11px] font-medium ${
            isMobileMenuOpen || !isActivePrimaryItem ? "text-brand-700" : "text-slate-500"
          }`}
        >
          <Menu size={18} />
          Mais
        </button>
        {mobileBellSlot}
        {mobileUserSlot}
      </nav>

      {activeView === "login" ? (
        <button
          type="button"
          onClick={() => onNavigate("dashboard")}
          className="fixed bottom-24 right-4 z-30 inline-flex items-center gap-2 rounded-full bg-ink px-4 py-3 text-sm font-semibold text-white shadow-panel lg:hidden"
        >
          <LogIn size={16} />
          Demo
        </button>
      ) : null}

      {activeView !== "login" && (helpButton || supportButton) ? (
        <div className="fixed bottom-24 right-4 z-40 flex flex-col items-end gap-3 lg:bottom-6" data-screenshot-ignore>
          {helpButton}
          {supportButton}
        </div>
      ) : null}
    </div>
  );
}
